/**
 * Кабинет: карта лояльности, история баллов, заказы и адреса доставки.
 *
 * Баланс и QR для кассы считает сервер — номер карты это номер телефона, и
 * собирать его самим нельзя: касса сканирует ровно ту строку, что пришла.
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@dorixona/api'
import { nowMs, parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { listenPushReceived } from '../../app/push'
import { toStore, type Store } from '../stores/api'

type OrderDto = components['schemas']['Order']
type LedgerDto = components['schemas']['LedgerEntry']
type AddressDto = components['schemas']['SavedAddress']

export interface Loyalty {
  balance: number
  phone: string
  /** Строка для кассы: её и кодируем в QR, не выдумывая свой формат. */
  qr: string
  /** Лимит оплаты баллами, % от суммы товаров. */
  maxSpendPct: number
  /**
   * Как касса узнаёт покупателя (R-58): `phone` — номер и копит, и тратит;
   * `phone_accrual` — номер только копит, тратить можно по одноразовому коду;
   * `code` — номер не принимается, на кассе только одноразовый код (`qr` пустой).
   */
  scanMode: ScanMode
}

export type ScanMode = 'phone' | 'phone_accrual' | 'code'

/** Одноразовый код для кассы: двенадцать цифр, гаснет после первого сканирования. */
export interface ScanCode {
  code: string
  /** Когда код перестанет действовать, мс эпохи. */
  expiresAt: number
  /** Разрешает ли код списать баллы. */
  spend: boolean
}

const scanModes: readonly ScanMode[] = ['phone', 'phone_accrual', 'code']

/** Неизвестный режим читаем как самый строгий из тех, где номер ещё работает. */
export function toScanMode(value: string | undefined): ScanMode {
  return scanModes.find((mode) => mode === value) ?? 'phone_accrual'
}

/**
 * Одноразовый код для кассы (`POST /me/loyalty/scan-code`). С `spend` покупатель
 * разрешает списать баллы именно этим сканированием; без — код только копит.
 * Живёт минуту: снимок экрана с ним ничего не стоит.
 */
export function useScanCode() {
  const api = useApi()
  return useMutation({
    mutationFn: async (spend: boolean): Promise<ScanCode> => {
      const dto = await api.post<{ code?: string; expires_in?: number; spend?: boolean }>(
        '/me/loyalty/scan-code',
        { spend },
      )
      return {
        code: dto.code ?? '',
        expiresAt: nowMs() + (dto.expires_in ?? 60) * 1000,
        spend: dto.spend ?? spend,
      }
    },
  })
}

/**
 * Тип движения баллов. `offline_spend` — оплата баллами на кассе аптеки: сервер
 * уже пишет его в историю, а в перечислении контракта (openapi) его пока нет.
 */
export type LedgerType = NonNullable<LedgerDto['type']> | 'offline_spend'

export interface Transaction {
  id: number
  type: LedgerType
  /** Со знаком: плюс — начислено, минус — списано. */
  amount: number
  orderId: number
  at: number
}

export type OrderStatus = NonNullable<OrderDto['status']>

export interface OrderLine {
  id: number
  productId: number
  name: string
  price: number
  qty: number
  total: number
}

export interface OrderEvent {
  id: number
  message: string
  at: number
  /** В какой статус перешёл заказ; пусто — событие без смены статуса. */
  status: string
}

export interface Courier {
  name: string
  phone: string
  /** Цвет, марка и модель одной строкой: «белый Chevrolet Cobalt». */
  car: string
  carNumber: string
}

/** Курьер доставки. Есть только у доставки и только после вызова курьера. */
export interface Delivery {
  /** Номер заявки в службе доставки — по нему покупатель звонит в службу. */
  tracking: string
  /** Что служба сообщила о курьере; пусто — курьера ещё ищут (R-44). */
  status: string
  courier: Courier | null
  /** Где машина по последнему сообщению службы и когда; null — точку ещё не присылали. */
  car: { lat: number; lng: number; at: number } | null
}

export interface Order {
  id: number
  number: number
  status: OrderStatus
  ship: 'pickup' | 'delivery'
  storeName: string
  /** Аптека заказа; null — ещё подбирается. */
  storeId: number | null
  /**
   * Карточка аптеки — та же, что в списке аптек: адрес, телефон, точка, часы.
   * Только в деталях заказа; null — заказ ещё не направлен или аптеку удалили.
   */
  store: Store | null
  address: string
  /** Куда везём: точка адреса доставки; null — у адреса нет точки или это самовывоз. */
  dest: { lat: number; lng: number } | null
  payMethod: string
  goods: number
  deliveryFee: number
  spend: number
  cashback: number
  total: number
  hasRx: boolean
  createdAt: number
  /** До какого времени аптека держит бронь, мс; 0 — брони нет (R-39). */
  reservationExpires: number
  /** Страница оплаты, пока заказ ждёт денег; после оплаты — пусто. */
  checkoutUrl: string
  /**
   * Что с деньгами: `held` — заблокированы на сохранённой карте, спишутся при
   * получении; `released` — вернулись на карту (R-105). Пусто — платежа не было.
   */
  payStatus: string
  lines: OrderLine[]
  events: OrderEvent[]
  delivery: Delivery | null
}

export interface Address {
  id: number
  /** Ярлык места («Дом», «Работа»); пусто — адрес показываем улицей (F-33). */
  tag: string
  label: string
  lat: number | null
  lng: number | null
  def: boolean
  used: number
}

/** Адрес доставки одной строкой: в заказе он приходит объектом. */
export function addressText(address: OrderDto['address']): string {
  if (!address || typeof address !== 'object') return ''
  const label = (address as { label?: unknown }).label
  return typeof label === 'string' ? label : ''
}

/** Точка аптеки заказа, если она известна: туда едет курьер, пока не заберёт заказ. */
export function storePoint(store: Store | null): { lat: number; lng: number } | null {
  if (!store || store.lat === null || store.lng === null) return null
  return { lat: store.lat, lng: store.lng }
}

/** Точка адреса доставки: в заказе адрес приходит объектом с lat и lng. */
export function addressPoint(address: OrderDto['address']): { lat: number; lng: number } | null {
  if (!address || typeof address !== 'object') return null
  const { lat, lng } = address as { lat?: unknown; lng?: unknown }
  return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null
}

export function toDelivery(dto: OrderDto['delivery']): Delivery | null {
  if (!dto) return null
  const c = dto.courier
  return {
    tracking: dto.tracking_number ?? '',
    status: dto.status ?? '',
    car:
      typeof dto.lat === 'number' && typeof dto.lng === 'number'
        ? { lat: dto.lat, lng: dto.lng, at: parseMs(dto.coords_at) }
        : null,
    courier: c
      ? {
          name: c.name ?? '',
          phone: c.phone ?? '',
          car: [c.car_color, c.car_mark, c.car_model].filter(Boolean).join(' '),
          carNumber: c.car_number ?? '',
        }
      : null,
  }
}

export function toOrder(dto: OrderDto): Order {
  return {
    id: dto.id ?? 0,
    number: dto.number ?? 0,
    status: dto.status ?? 'new',
    ship: dto.ship_method ?? 'pickup',
    storeName: dto.store ?? '',
    storeId: dto.store_id ?? null,
    store: dto.store_details
      ? toStore(dto.store_details as unknown as Parameters<typeof toStore>[0])
      : null,
    address: addressText(dto.address),
    dest: addressPoint(dto.address),
    payMethod: dto.pay_method ?? '',
    goods: dto.goods_total ?? 0,
    deliveryFee: dto.delivery_fee ?? 0,
    spend: dto.spend_amount ?? 0,
    cashback: dto.cashback_amount ?? 0,
    total: dto.total_to_pay ?? 0,
    hasRx: dto.has_rx ?? false,
    createdAt: parseMs(dto.created_at),
    reservationExpires: parseMs(dto.reservation_expires_at),
    // Ссылка на оплату остаётся в заказе и после оплаты — «оплатить» зовём,
    // только пока заказ действительно ждёт денег.
    checkoutUrl:
      dto.status === 'awaiting_payment' && dto.pay_status !== 'paid'
        ? (dto.checkout_url ?? '')
        : '',
    payStatus: dto.pay_status ?? '',
    lines: (dto.lines ?? []).map((l) => ({
      id: l.id ?? 0,
      productId: l.product_id ?? 0,
      name: l.name ?? '',
      price: l.price ?? 0,
      qty: l.qty ?? 0,
      total: l.line_total ?? 0,
    })),
    events: (dto.events ?? []).map((e) => ({
      id: e.id ?? 0,
      message: e.message ?? '',
      at: parseMs(e.created_at),
      status: e.to_status ?? '',
    })),
    delivery: toDelivery(dto.delivery),
  }
}

export function toTransaction(dto: LedgerDto): Transaction {
  return {
    id: dto.id ?? 0,
    type: dto.type ?? 'order_accrual',
    amount: dto.amount ?? 0,
    orderId: dto.order_id ?? 0,
    at: parseMs(dto.created_at),
  }
}

export function toAddress(dto: AddressDto): Address {
  return {
    id: dto.id ?? 0,
    tag: dto.tag ?? '',
    label: dto.label,
    lat: dto.lat ?? null,
    lng: dto.lng ?? null,
    def: dto.is_default ?? false,
    used: parseMs(dto.used_at),
  }
}

export function useLoyalty(signedIn: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.loyalty,
    queryFn: async () => {
      const dto = await api.get<{
        balance?: number
        phone?: string
        qr?: string
        max_spend_pct?: number
        scan_mode?: string
      }>('/me/loyalty')
      return {
        balance: dto.balance ?? 0,
        phone: dto.phone ?? '',
        qr: dto.qr ?? '',
        maxSpendPct: dto.max_spend_pct ?? 0,
        scanMode: toScanMode(dto.scan_mode),
      } satisfies Loyalty
    },
    enabled: signedIn,
    // Вернулись в приложение — баланс мог измениться на кассе, пока его не было.
    refetchOnWindowFocus: 'always',
  })
}

/**
 * Баланс без перезапуска: пришёл push — перечитываем `/me/loyalty`. Кэшбэк,
 * списание и бонусы приходят push-ом, статусы заказа тоже двигают баллы, а
 * запрос дешёвый — поэтому любой push, без разбора шаблона. Событие шлёт
 * оболочка (docs/notes/flutter-bridge.md › Push); без неё баланс догонит опрос
 * непрочитанных и возврат в приложение.
 */
export function useBalanceOnPush(signedIn: boolean) {
  const client = useQueryClient()
  useEffect(() => {
    if (!signedIn) return
    return listenPushReceived(() => {
      void client.invalidateQueries({ queryKey: keys.loyalty })
      void client.invalidateQueries({ queryKey: keys.unread })
    })
  }, [client, signedIn])
}

export function useTransactions(signedIn: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.transactions,
    queryFn: async () => {
      const page = await api.get<{ items?: LedgerDto[] }>('/me/loyalty/transactions', {
        query: { page_size: 50 },
      })
      return (page.items ?? []).map(toTransaction)
    },
    enabled: signedIn,
  })
}

export function useOrders(
  signedIn: boolean,
  refetchInterval: (orders: Order[] | undefined) => number | false = () => false,
) {
  const api = useApi()
  return useQuery({
    queryKey: keys.orders,
    queryFn: async () => {
      const page = await api.get<{ items?: OrderDto[] }>('/orders', { query: { page_size: 50 } })
      return (page.items ?? []).map(toOrder)
    },
    enabled: signedIn,
    refetchInterval: (query) => refetchInterval(query.state.data),
  })
}

/** Как часто перечитывать заказ, пока его ведут аптека и курьер. */
export const TRACK_MS = 30_000

/** Пока заказ ждёт денег — чаще: покупатель платит в соседней вкладке и ждёт ответа. */
export const PAY_WAIT_MS = 5_000

/** Статусы, после которых с заказом больше ничего не происходит. */
const FINAL: OrderStatus[] = [
  'delivered',
  'issued',
  'payment_expired',
  'reservation_expired',
  'cancelled',
]

/**
 * Как часто перечитывать открытый заказ: статус меняют оплата, аптека и курьер,
 * а push пока не отправляется. Закрытый заказ не перечитываем.
 */
export function refetchMs(order: Order): number | false {
  if (order.status === 'awaiting_payment') return PAY_WAIT_MS
  return FINAL.includes(order.status) ? false : TRACK_MS
}

/** Заказ ещё в работе: ждёт оплаты, собирается, едет или ждёт в аптеке. */
export function isActiveOrder(order: Order): boolean {
  return !FINAL.includes(order.status)
}

/**
 * Сколько заказов ещё в работе — для счётчика на вкладке «Заказы». Пока такой
 * есть, список перечитываем: заказ закроется, и счётчик должен уйти сам.
 */
export function useActiveOrderCount(signedIn: boolean): number {
  const orders = useOrders(signedIn, (list) => (list?.some(isActiveOrder) ? TRACK_MS : false))
  return signedIn ? (orders.data?.filter(isActiveOrder).length ?? 0) : 0
}

export function useOrder(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.order(id),
    queryFn: async () => toOrder(await api.get<OrderDto>(`/orders/${id}`)),
    enabled: id > 0,
    refetchInterval: (query) => (query.state.data ? refetchMs(query.state.data) : false),
  })
}

/** Повторить заказ: сервер кладёт в корзину то, что ещё продаётся (R-48). */
export function useRepeatOrder() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<unknown>(`/orders/${id}/repeat`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.cart }),
  })
}

export function useAddresses(signedIn: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.addresses,
    queryFn: async () => {
      const page = await api.get<{ items?: AddressDto[] }>('/me/addresses')
      return (page.items ?? []).map(toAddress)
    },
    enabled: signedIn,
  })
}

/** Что покупатель меняет в адресе: улица и точка, ярлык и «основной». */
export interface AddressInput {
  tag: string
  label: string
  lat: number | null
  lng: number | null
  /** Сделать основным: его checkout подставляет сам. Снять флаг нельзя — только отметить другой. */
  makeDefault: boolean
}

const addressBody = (input: AddressInput) => ({
  tag: input.tag.trim(),
  label: input.label.trim(),
  lat: input.lat,
  lng: input.lng,
})

/**
 * Новый адрес (F-33). Сервер отвечает без номера, а основным адрес делают
 * по номеру, поэтому его находим в свежем списке — улица у покупателя одна
 * на адрес, сервер повторов не хранит.
 */
export function useSaveAddress() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: AddressInput) => {
      const body = addressBody(input)
      await api.post<unknown>('/me/addresses', body)
      if (!input.makeDefault) return
      const page = await api.get<{ items?: AddressDto[] }>('/me/addresses')
      const saved = (page.items ?? []).find((dto) => dto.label === body.label)
      if (saved?.id) await api.post<unknown>(`/me/addresses/${saved.id}/default`)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.addresses }),
  })
}

/** Переименовать, перенести или сделать основным сохранённый адрес. */
export function useUpdateAddress() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: AddressInput & { id: number }) => {
      await api.put<unknown>(`/me/addresses/${id}`, addressBody(input))
      if (input.makeDefault) await api.post<unknown>(`/me/addresses/${id}/default`)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.addresses }),
  })
}

/** Забыть адрес: из кабинета и из выбора на оформлении. */
export function useDeleteAddress() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.del<unknown>(`/me/addresses/${id}`)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.addresses }),
  })
}

export interface ProfilePatch {
  firstName: string
  lastName: string
  gender: 'male' | 'female'
  lang: 'ru' | 'uz' | 'en'
  /** Дату рождения покупатель ставит один раз; пустая — не трогаем (R-56). */
  birth: string
  /** Уведомления меняют в настройках; не передали — сервер оставит как есть. */
  notifyPush?: boolean
}

export function useSaveProfile() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patch: ProfilePatch) =>
      api.patch<components['schemas']['Customer']>('/me', {
        first_name: patch.firstName,
        last_name: patch.lastName,
        gender: patch.gender,
        language: patch.lang,
        birth_date: patch.birth || undefined,
        notify_push: patch.notifyPush,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['me'] }),
  })
}

/**
 * Удалить свой аккаунт (`DELETE /me`, R-114). Сессии гаснут сразу, войти в
 * этот аккаунт больше нельзя; тот же номер при новом входе заведёт новый,
 * пустой аккаунт — без баллов, заказов, адресов и карт. Пока есть незавершённый
 * заказ, сервер откажет с `orders_open`.
 */
export function useDeleteAccount() {
  const api = useApi()
  return useMutation({
    mutationFn: () => api.del<unknown>('/me'),
  })
}
